// Gráficos de domínio — 100% calculados a partir do xlsx (dados reais).
// Usados dentro das abas Microgestão Campo / Atendimento.
import { useMemo } from "react";
import { alpha } from "@mui/material/styles";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import {
  Bar,
  BarChart,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import {
  CircleMarker,
  MapContainer,
  TileLayer,
  Tooltip as LeafletTooltip,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { formatInt, formatPct } from "./shared";
import { pctColor } from "./analytics";
import { tecnicos, cidades } from "./microgestaoData";
import { cidadesGeo } from "./geoData";
import { custoMedioOS } from "./simulatedData";

export const brl = (v) =>
  Number(v || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });

const shortName = (nome, max = 22) => {
  const s = String(nome || "").replace(/^[A-Z]{2}\d{2}-[A-Z-]+\s*-?\s*/i, "");
  const base = s || String(nome || "");
  return base.length > max ? `${base.slice(0, max)}…` : base;
};

const filialFromNome = (nome) => {
  const m = String(nome || "").match(/\b(GO|DF|MT)\s*-?\s*0?(\d)\b/i);
  return m ? `${m[1].toUpperCase()} 0${m[2]}` : null;
};

/* ----------------------- Mapa de reincidência ----------------------- */
// Normaliza nome para casar regional (API) × cidade (geoData): sem acento, minúsculo.
const normNome = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

export function MapaReincidencia({ styles, theme, liveRegionais = [] }) {
  // Coordenadas vêm do geoData (estático); pct/volume/reinc são sobrescritos
  // pelos valores reais da API (endpoint /regional) sempre que o nome casar.
  const { pontos, liveCount } = useMemo(() => {
    const live = (liveRegionais || [])
      .map((r) => ({
        nome: normNome(r.regional),
        pct: r.p90 ?? 0,
        volume: r.base ?? 0,
        reinc: r.r90 ?? 0,
      }))
      .filter((r) => r.nome && r.nome !== "nao cadastrado");

    let liveCount = 0;
    const pontos = cidadesGeo.map((c) => {
      const cidadeNorm = normNome(c.cidade);
      const match = live.find(
        (r) => r.nome === cidadeNorm || r.nome.includes(cidadeNorm),
      );
      if (match && match.volume > 0) {
        liveCount += 1;
        return { ...c, pct: match.pct, volume: match.volume, reinc: match.reinc, live: true };
      }
      return { ...c, live: false };
    });
    return { pontos, liveCount };
  }, [liveRegionais]);

  const temLive = liveCount > 0;

  return (
    <Box>
      <Box sx={{ position: "relative", height: 420, borderRadius: "12px", overflow: "hidden" }}>
        <MapContainer
          center={[-15.5, -50]}
          zoom={5}
          scrollWheelZoom
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap"
          />
          {pontos.map((c) => {
            // Sem dado live (quando já existe live em outras cidades) → marcador "vazado".
            const stale = temLive && !c.live;
            return (
              <CircleMarker
                key={c.cidade}
                center={[c.lat, c.lng]}
                radius={4 + Math.sqrt(c.volume) * 0.5}
                pathOptions={{
                  color: pctColor(theme, c.pct),
                  fillColor: pctColor(theme, c.pct),
                  fillOpacity: stale ? 0.12 : 0.55,
                  weight: stale ? 1 : 1.5,
                  dashArray: stale ? "3 3" : undefined,
                }}
              >
                <LeafletTooltip direction="top">
                  <div style={{ fontWeight: 700 }}>
                    {c.cidade} ({c.filial})
                  </div>
                  <div>{formatPct(c.pct)} reincidência</div>
                  <div>
                    {formatInt(c.volume)} chamados • {formatInt(c.reinc)} reinc.
                  </div>
                  {stale && (
                    <div style={{ opacity: 0.7, marginTop: 2 }}>sem dado no período</div>
                  )}
                </LeafletTooltip>
              </CircleMarker>
            );
          })}
        </MapContainer>

        {/* Legenda sobre o mapa */}
        <Box
          sx={{
            position: "absolute",
            bottom: 12,
            right: 12,
            zIndex: 1000,
            p: "8px 10px",
            borderRadius: "8px",
            backgroundColor: alpha(theme.palette.background.paper, 0.92),
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[2],
            pointerEvents: "none",
          }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 800, mb: 0.5 }}>
            Reincidência
          </Typography>
          <Stack gap={0.5}>
            {[
              { c: theme.palette.error.main, t: "≥ 30%" },
              { c: theme.palette.warning.main, t: "20–30%" },
              { c: theme.palette.success.main, t: "< 20%" },
            ].map((l) => (
              <Stack key={l.t} direction="row" gap={0.75} alignItems="center">
                <Box sx={{ width: 11, height: 11, borderRadius: "50%", bgcolor: l.c }} />
                <Typography sx={{ fontSize: 11, color: theme.palette.text.secondary }}>
                  {l.t}
                </Typography>
              </Stack>
            ))}
            <Stack direction="row" gap={0.75} alignItems="center" sx={{ mt: 0.25 }}>
              <Box
                sx={{
                  width: 11,
                  height: 11,
                  borderRadius: "50%",
                  border: `1px dashed ${theme.palette.text.disabled}`,
                }}
              />
              <Typography sx={{ fontSize: 11, color: theme.palette.text.secondary }}>
                sem dado live
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Box>

      <Typography sx={{ ...styles.cardSubtitle, mt: 1 }}>
        • tamanho = volume • coordenadas aproximadas •{" "}
        {temLive
          ? `${liveCount} de ${pontos.length} cidades com dado real do período`
          : "exibindo snapshot estático (sem dado live para o período/filtro)"}
      </Typography>
    </Box>
  );
}

/* ----------------------- Efeito Técnico vs Rede ----------------------- */
export function EfeitoTecnicoCidade({ styles, theme }) {
  const grupos = useMemo(() => {
    const map = new Map();
    tecnicos.forEach((t) => {
      const f = filialFromNome(t.nome);
      if (!f) return;
      if (!map.has(f)) map.set(f, []);
      map.get(f).push({ nome: t.nome, pct: t.pct * 100 });
    });
    const out = [];
    map.forEach((itens, filial) => {
      if (itens.length < 3) return;
      const pcts = itens.map((it) => it.pct);
      const media = pcts.reduce((s, v) => s + v, 0) / pcts.length;
      const std = Math.sqrt(
        pcts.reduce((s, v) => s + (v - media) ** 2, 0) / pcts.length,
      );
      const cv = media ? std / media : 0;
      out.push({
        filial,
        n: pcts.length,
        media,
        cv,
        min: Math.min(...pcts),
        max: Math.max(...pcts),
        itens,
        efeito: cv >= 0.4 ? "Pessoa" : cv <= 0.25 ? "Rede" : "Misto",
      });
    });
    return out.sort((a, b) => b.cv - a.cv);
  }, []);

  const escalaMax = Math.max(...grupos.map((g) => g.max), 50);
  const efeitoColor = {
    Pessoa: theme.palette.error.main,
    Misto: theme.palette.warning.main,
    Rede: theme.palette.info.main,
  };

  return (
    <Stack gap={1.5}>
      <Typography sx={styles.cardSubtitle}>
        Alta dispersão entre técnicos da mesma filial → problema é a <b>pessoa</b>.
        Baixa dispersão → problema é a <b>rede/infra</b> da região.
      </Typography>
      {grupos.map((g) => (
        <Box key={g.filial}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
              {g.filial}{" "}
              <Typography component="span" sx={styles.cardSubtitle}>
                ({g.n} técnicos • média {g.media.toFixed(1)}%)
              </Typography>
            </Typography>
            <Chip
              size="small"
              label={`Efeito ${g.efeito}`}
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 800,
                color: efeitoColor[g.efeito],
                backgroundColor: alpha(efeitoColor[g.efeito], 0.14),
              }}
            />
          </Stack>
          <Box
            sx={{
              position: "relative",
              height: 26,
              mt: 0.5,
              borderRadius: "8px",
              backgroundColor: alpha(theme.palette.text.primary, 0.05),
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: "35%",
                height: 6,
                borderRadius: 3,
                left: `${(g.min / escalaMax) * 100}%`,
                width: `${((g.max - g.min) / escalaMax) * 100}%`,
                backgroundColor: alpha(efeitoColor[g.efeito], 0.3),
                pointerEvents: "none",
              }}
            />
            {g.itens.map((it, i) => (
              <Tooltip
                key={i}
                arrow
                placement="top"
                title={`${shortName(it.nome, 40)} — ${it.pct.toFixed(1)}%`}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    left: `${(it.pct / escalaMax) * 100}%`,
                    width: 11,
                    height: 11,
                    borderRadius: "50%",
                    backgroundColor: pctColor(theme, it.pct / 100),
                    border: `1px solid ${theme.palette.background.paper}`,
                    cursor: "pointer",
                    transition: "transform .1s",
                    "&:hover": {
                      transform: "translate(-50%, -50%) scale(1.7)",
                      zIndex: 3,
                    },
                  }}
                />
              </Tooltip>
            ))}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `${(g.media / escalaMax) * 100}%`,
                width: 2,
                backgroundColor: theme.palette.text.primary,
                pointerEvents: "none",
              }}
            />
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

/* ----------------------- TMT × % Reincidência ----------------------- */
export function TmtScatter({ styles, theme, rows }) {
  const { data, segmento } = useMemo(() => {
    const data = rows
      .filter((r) => r.tmt && (r.volume || 0) >= 5)
      .map((r) => ({
        x: +(r.tmt / 3600).toFixed(1),
        y: +((r.pct || 0) * 100).toFixed(1),
        z: r.volume || 0,
        label: shortName(r.nome),
      }));
    const n = data.length || 1;
    const sx = data.reduce((s, d) => s + d.x, 0);
    const sy = data.reduce((s, d) => s + d.y, 0);
    const sxy = data.reduce((s, d) => s + d.x * d.y, 0);
    const sxx = data.reduce((s, d) => s + d.x * d.x, 0);
    const b = (n * sxy - sx * sy) / (n * sxx - sx * sx || 1);
    const a = (sy - b * sx) / n;
    const xs = data.map((d) => d.x);
    const x0 = Math.min(...xs);
    const x1 = Math.max(...xs);
    return {
      data,
      segmento: [
        { x: x0, y: +(a + b * x0).toFixed(1) },
        { x: x1, y: +(a + b * x1).toFixed(1) },
      ],
    };
  }, [rows]);

  return (
    <Box sx={styles.chartBox}>
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ left: 0, right: 16, top: 12, bottom: 8 }}>
          <XAxis
            type="number"
            dataKey="x"
            name="TMT (h)"
            tickFormatter={(v) => `${v}h`}
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="number"
            dataKey="y"
            tickFormatter={(v) => `${v}%`}
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            axisLine={false}
            tickLine={false}
          />
          <ZAxis type="number" dataKey="z" range={[30, 400]} />
          <ReferenceLine
            segment={segmento}
            stroke={theme.palette.primary.main}
            strokeWidth={2}
            strokeDasharray="5 5"
            ifOverflow="extendDomain"
          />
          <RechartsTooltip
            cursor={{ strokeDasharray: "3 3" }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0].payload;
              return (
                <Box sx={{ ...styles.chartTooltip, p: "8px 10px" }}>
                  <Box sx={{ fontSize: 12, fontWeight: 700 }}>{d.label}</Box>
                  <Box sx={{ fontSize: 12 }}>
                    {d.y}% • {d.x}h TMT
                  </Box>
                  <Box sx={{ fontSize: 12 }}>{formatInt(d.z)} chamados</Box>
                </Box>
              );
            }}
          />
          <Scatter data={data} fillOpacity={0.6}>
            {data.map((d, i) => (
              <Cell key={i} fill={pctColor(theme, d.y / 100)} />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
    </Box>
  );
}

/* ----------------------- Custo de retrabalho por filial ----------------------- */
const agruparFilial = () => {
  const map = new Map();
  cidades.forEach((c) => {
    const k = c.filial || "—";
    map.set(k, (map.get(k) || 0) + (c.reinc || 0));
  });
  return [...map.entries()]
    .map(([filial, reinc]) => ({ filial, reinc, custo: reinc * custoMedioOS }))
    .sort((a, b) => b.reinc - a.reinc);
};

export function FinanceiroFilial({ styles, theme }) {
  const data = useMemo(agruparFilial, []);
  return (
    <Box sx={styles.chartBox}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ left: 8, right: 8, top: 8, bottom: 4 }}>
          <XAxis
            dataKey="filial"
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            axisLine={false}
            tickLine={false}
          />
          <RechartsTooltip
            contentStyle={styles.chartTooltip}
            formatter={(value, _n, p) => [
              `${brl(value)}  •  ${formatInt(p?.payload?.reinc)} reinc.`,
              "Retrabalho",
            ]}
            cursor={{ fill: alpha(theme.palette.text.primary, 0.04) }}
          />
          <Bar
            dataKey="custo"
            radius={[6, 6, 0, 0]}
            barSize={34}
            fill={theme.palette.error.main}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

export const custoRetrabalhoTotal = () =>
  tecnicos.reduce((s, r) => s + (r.reinc || 0), 0) * custoMedioOS;
